import { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Building2, Home, KeyRound, DoorOpen, History, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { STATUS_META, formatDate } from "@/lib/status";

const KPI = ({ icon: Icon, label, value, accent, testid, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: index * 0.05 }}
    className="col-span-1 rounded-xl p-5 bg-white border border-slate-200/80 shadow-sm flex flex-col justify-between min-h-[130px]"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase font-mono tracking-[0.18em] text-slate-400">{label}</span>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <span className="text-3xl font-bold font-mono tracking-tight text-slate-900 tabular-nums">{value}</span>
  </motion.div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/seed");
      toast.success(data.message || `Seeded ${data.flats} flats`);
      await load();
    } catch {
      toast.error("Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading telemetry…
      </div>
    );
  }

  const maxTower = Math.max(1, ...(stats?.by_tower || []).map((t) => t.total));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase font-mono tracking-[0.18em] text-slate-400 mb-1">Society Overview</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Occupancy Dashboard</h1>
        </div>
        {stats?.total_flats === 0 && (
          <Button onClick={handleSeed} disabled={seeding} data-testid="seed-data-button" className="bg-[#0F2942] gap-2">
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Load sample society data
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <KPI index={0} icon={Building2} label="Total Flats" value={stats.total_flats} accent="bg-slate-100 text-slate-700" testid="stat-total-flats" />
        <KPI index={1} icon={KeyRound} label="Currently Rented" value={stats.rented_flats} accent="bg-emerald-50 text-emerald-600" testid="stat-rented-flats" />
        <KPI index={2} icon={Home} label="Owner Occupied" value={stats.owner_occupied_flats} accent="bg-blue-50 text-blue-600" testid="stat-owner-occupied-flats" />
        <KPI index={3} icon={DoorOpen} label="Vacant / Available" value={stats.vacant_flats} accent="bg-amber-50 text-amber-600" testid="stat-vacant-flats" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tower occupancy */}
        <div className="lg:col-span-2 rounded-xl p-6 bg-white border border-slate-200/80 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-lg font-semibold text-slate-900">Occupancy by Tower</h2>
            <span className="text-[11px] uppercase font-mono tracking-[0.18em] text-slate-400" data-testid="stat-total-tenancy-cycles">
              {stats.total_tenancy_cycles} tenancy cycles logged
            </span>
          </div>
          <div className="space-y-5">
            {(stats.by_tower || []).map((t) => (
              <div key={t.tower} data-testid={`tower-bar-${t.tower}`}>
                <div className="flex items-center justify-between mb-1.5 text-sm">
                  <span className="font-semibold text-slate-700">Tower {t.tower} — {t.tower_name}</span>
                  <span className="font-mono text-slate-500 text-xs tabular-nums">{t.total} units</span>
                </div>
                <div className="flex h-3 w-full rounded-full overflow-hidden bg-slate-100" style={{ width: `${(t.total / maxTower) * 100}%`, minWidth: "30%" }}>
                  <div className="bg-emerald-500" style={{ width: `${(t.rented / t.total) * 100}%` }} title={`Rented: ${t.rented}`} />
                  <div className="bg-blue-500" style={{ width: `${(t.owner / t.total) * 100}%` }} title={`Owner: ${t.owner}`} />
                  <div className="bg-amber-400" style={{ width: `${(t.vacant / t.total) * 100}%` }} title={`Vacant: ${t.vacant}`} />
                </div>
              </div>
            ))}
            {(!stats.by_tower || stats.by_tower.length === 0) && (
              <p className="text-sm text-slate-400">No flats yet. Add flats in the Inventory tab or load sample data.</p>
            )}
          </div>
          <div className="flex gap-5 mt-6 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Rented</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Owner</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Vacant</span>
          </div>
        </div>

        {/* Recent tenancy activity */}
        <div className="rounded-xl p-6 bg-white border border-slate-200/80 shadow-sm">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
            <History className="h-4 w-4 text-slate-400" /> Recent Tenancy Activity
          </h2>
          <div className="space-y-4" data-testid="recent-activity-feed">
            {(stats.recent_tenancies || []).map((r) => {
              const meta = r.status === "active" ? STATUS_META.rented : STATUS_META.vacant;
              return (
                <div key={r.id} className="flex items-start gap-3">
                  <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.tenant_name}</p>
                    <p className="text-xs text-slate-400 font-mono">
                      {r.flat_label} · {r.status === "active" ? "Active" : "Past"} · in {formatDate(r.move_in_date)}
                    </p>
                  </div>
                </div>
              );
            })}
            {(!stats.recent_tenancies || stats.recent_tenancies.length === 0) && (
              <p className="text-sm text-slate-400">No tenancy records yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
